// src/Components/AdminVerificationDashboard.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import API_BASE_URL from '../config';

const AdminVerificationDashboard = () => {
  const [verificationData, setVerificationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadVerificationData();
  }, []);

  const loadVerificationData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/applications/admin/verification-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        console.log('✅ Verification data loaded:', response.data.data);
        setVerificationData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading verification data:', error);
      alert('Failed to load verification data');
    } finally {
      setLoading(false);
    }
  };

  const viewCandidateDetails = async (applicationId) => {
    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/applications/verification/${applicationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        console.log('✅ Candidate details:', response.data.data);
        setSelectedCandidate(response.data.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error loading candidate details:', error);
      alert('Failed to load candidate details');
    }
  };

  const getPhotoUrl = (photoVerification) => {
    if (!photoVerification || !photoVerification.captured) return null;
    if (photoVerification.photoUrl) return photoVerification.photoUrl;
    if (photoVerification.photoData) return photoVerification.photoData;
    return null;
  };

  const exportToExcel = () => {
    const rows = verificationData.map(c => ({
      'Application ID': c.applicationId || '',
      'Candidate Name': c.candidateName || '',
      'Email': c.candidateEmail || '',
      'Position': c.position || '',
      'Company': c.companyName || '',
      'Photo Captured': c.photoVerification?.captured ? 'Yes' : 'No',
      'Face Detected': c.photoVerification?.faceDetected ? 'Yes' : 'No',
      'Photo Quality': c.photoVerification?.quality || '',
      'Photo Captured At': c.photoVerification?.capturedAt
        ? new Date(c.photoVerification.capturedAt).toLocaleString() : '',
      'Screen Share Completed': c.screenShare?.completed ? 'Yes' : 'No',
      'System Audio Shared': c.screenShare?.systemAudioShared ? 'Yes' : 'No',
      'Screen Share Started': c.screenShare?.startedAt
        ? new Date(c.screenShare.startedAt).toLocaleString() : '',
      'Screen Share Ended': c.screenShare?.endedAt
        ? new Date(c.screenShare.endedAt).toLocaleString() : '',
      'System Check Passed': c.systemCheck?.passed ? 'Yes' : 'No',
      'Camera Detected': c.systemCheck?.cameraDetected ? 'Yes' : 'No',
      'Microphone Detected': c.systemCheck?.microphoneDetected ? 'Yes' : 'No',
      'Internet Speed': c.systemCheck?.internetSpeed || '',
      'All Steps Completed': c.allStepsCompleted ? 'Yes' : 'No',
      'Started At': c.startedAt ? new Date(c.startedAt).toLocaleString() : '',
      'Completed At': c.completedAt ? new Date(c.completedAt).toLocaleString() : '',
      'Overall Status': c.allStepsCompleted ? 'Completed' : c.photoVerification?.captured ? 'Partial' : 'Pending'
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0] || {}).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, 'Verification Data', ws);
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `candidate_verification_${date}.xlsx`);
  };

  const getStatusBadge = (candidate) => {
    if (candidate.allStepsCompleted) {
      return (
        <span style={{
          padding: '4px 10px', borderRadius: '20px', fontSize: '12px',
          fontWeight: 500, background: '#D1FAE5', color: '#065F46'
        }}>Completed</span>
      );
    } else if (candidate.photoVerification?.captured) {
      return (
        <span style={{
          padding: '4px 10px', borderRadius: '20px', fontSize: '12px',
          fontWeight: 500, background: '#FEF3C7', color: '#92400E'
        }}>Partial</span>
      );
    } else {
      return (
        <span style={{
          padding: '4px 10px', borderRadius: '20px', fontSize: '12px',
          fontWeight: 500, background: '#F3F4F6', color: '#374151'
        }}>Pending</span>
      );
    }
  };

  const getPhotoStatus = (candidate) => {
    if (candidate.photoVerification?.captured) {
      return <span style={{ color: '#16A34A', fontSize: '13px', fontWeight: 500 }}>✓ Captured</span>;
    }
    return <span style={{ color: '#DC2626', fontSize: '13px', fontWeight: 500 }}>✗ Not Captured</span>;
  };

  const getScreenShareStatus = (candidate) => {
    if (candidate.screenShare?.completed) {
      return <span style={{ color: '#16A34A', fontSize: '13px', fontWeight: 500 }}>✓ Shared</span>;
    }
    return <span style={{ color: '#DC2626', fontSize: '13px', fontWeight: 500 }}>✗ Not Shared</span>;
  };

  const filteredData = verificationData.filter(candidate => {
    const matchesSearch = searchTerm === '' ||
      candidate.candidateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.candidateEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.position?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filter === 'all' ||
      (filter === 'completed' && candidate.allStepsCompleted) ||
      (filter === 'partial' && !candidate.allStepsCompleted && candidate.photoVerification?.captured) ||
      (filter === 'pending' && !candidate.photoVerification?.captured);

    return matchesSearch && matchesFilter;
  });

  const avatarColors = [
    { bg: '#EFF6FF', color: '#1D4ED8' },
    { bg: '#F0FDF4', color: '#166534' },
    { bg: '#FFF7ED', color: '#C2410C' },
    { bg: '#FDF4FF', color: '#7E22CE' },
    { bg: '#ECFDF5', color: '#065F46' },
  ];

  if (loading) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '300px', background: '#fff'
      }}>
        <div style={{
          width: '36px', height: '36px', border: '3px solid #E5E7EB',
          borderTop: '3px solid #3B82F6', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        <p style={{ marginTop: '16px', color: '#6B7280', fontSize: '14px' }}>Loading verification data...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: '#fff', minHeight: '100vh', marginLeft: '270px' }}>

      {/* Header */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #E5E7EB',
        padding: '20px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#111827', letterSpacing: '-0.3px' }}>
            Candidate Verification Dashboard
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>
            Review photo captures and screen share activities
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={loadVerificationData}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', border: '1px solid #E5E7EB', borderRadius: '8px',
              background: '#fff', color: '#374151', fontSize: '13px', fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M8 16H3v5" />
            </svg>
            Refresh
          </button>
          <button
            onClick={exportToExcel}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', border: 'none', borderRadius: '8px',
              background: '#16A34A', color: '#fff', fontSize: '13px', fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export Excel
          </button>
        </div>
      </div>

      <div style={{ padding: '24px 28px' }}>

        {/* Statistics Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={{
            background: '#F9FAFB', border: '1px solid #E5E7EB',
            borderRadius: '10px', padding: '18px 20px'
          }}>
            <div style={{ fontSize: '26px', fontWeight: 600, color: '#111827' }}>{verificationData.length}</div>
            <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>Total Candidates</div>
          </div>
          <div style={{
            background: '#F0FDF4', border: '1px solid #BBF7D0',
            borderRadius: '10px', padding: '18px 20px'
          }}>
            <div style={{ fontSize: '26px', fontWeight: 600, color: '#15803D' }}>
              {verificationData.filter(c => c.allStepsCompleted).length}
            </div>
            <div style={{ fontSize: '13px', color: '#166534', marginTop: '2px' }}>Completed</div>
          </div>
          <div style={{
            background: '#EFF6FF', border: '1px solid #BFDBFE',
            borderRadius: '10px', padding: '18px 20px'
          }}>
            <div style={{ fontSize: '26px', fontWeight: 600, color: '#1D4ED8' }}>
              {verificationData.filter(c => c.photoVerification?.captured).length}
            </div>
            <div style={{ fontSize: '13px', color: '#1E40AF', marginTop: '2px' }}>Photo Captured</div>
          </div>
          <div style={{
            background: '#FFF7ED', border: '1px solid #FED7AA',
            borderRadius: '10px', padding: '18px 20px'
          }}>
            <div style={{ fontSize: '26px', fontWeight: 600, color: '#C2410C' }}>
              {verificationData.filter(c => c.screenShare?.completed).length}
            </div>
            <div style={{ fontSize: '13px', color: '#9A3412', marginTop: '2px' }}>Screen Shared</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
            <svg style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, email, or position..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%', padding: '9px 12px 9px 34px', border: '1px solid #E5E7EB',
                borderRadius: '8px', fontSize: '13px', color: '#374151', outline: 'none',
                fontFamily: 'inherit', boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {['all', 'completed', 'partial', 'pending'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit',
                  border: filter === f ? '1px solid #3B82F6' : '1px solid #E5E7EB',
                  background: filter === f ? '#3B82F6' : '#fff',
                  color: filter === f ? '#fff' : '#374151'
                }}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Verification Table */}
        <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                {['Candidate', 'Position', 'Photo', 'Screen Share', 'System Check', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 500, color: '#6B7280' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((candidate, index) => {
                const colorSet = avatarColors[index % avatarColors.length];
                const initials = (candidate.candidateName || 'C')
                  .split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                return (
                  <tr
                    key={candidate.applicationId || index}
                    style={{ borderBottom: '1px solid #F3F4F6', background: '#fff' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '34px', height: '34px', borderRadius: '50%',
                          background: colorSet.bg, color: colorSet.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12px', fontWeight: 600, flexShrink: 0
                        }}>
                          {initials}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, color: '#111827' }}>{candidate.candidateName}</div>
                          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '1px' }}>{candidate.candidateEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ color: '#374151', fontWeight: 500 }}>{candidate.position}</div>
                      <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '1px' }}>{candidate.companyName}</div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>{getPhotoStatus(candidate)}</td>
                    <td style={{ padding: '14px 16px' }}>{getScreenShareStatus(candidate)}</td>
                    <td style={{ padding: '14px 16px' }}>
                      {candidate.systemCheck?.passed
                        ? <span style={{ color: '#16A34A', fontSize: '13px', fontWeight: 500 }}>✓ Passed</span>
                        : <span style={{ color: '#DC2626', fontSize: '13px', fontWeight: 500 }}>✗ Failed</span>}
                    </td>
                    <td style={{ padding: '14px 16px' }}>{getStatusBadge(candidate)}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <button
                        onClick={() => viewCandidateDetails(candidate.applicationId)}
                        style={{
                          padding: '7px 14px', border: '1px solid #E5E7EB', borderRadius: '7px',
                          background: '#fff', color: '#374151', fontSize: '12px', fontWeight: 500,
                          cursor: 'pointer', fontFamily: 'inherit'
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: '48px', textAlign: 'center', color: '#9CA3AF' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5"
                      style={{ margin: '0 auto 12px', display: 'block' }}>
                      <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                    </svg>
                    <p style={{ margin: 0, fontSize: '14px' }}>No candidates found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedCandidate && (
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '14px', width: '620px',
              maxWidth: '95vw', maxHeight: '85vh', overflow: 'hidden',
              display: 'flex', flexDirection: 'column'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                Verification Details — {selectedCandidate.application?.name}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', padding: '4px', borderRadius: '6px' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>

              {/* Photo Verification */}
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.5px', marginBottom: '12px' }}>
                  PHOTO VERIFICATION
                </div>
                {selectedCandidate.photoVerification?.captured ? (
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    <div style={{ width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E5E7EB', flexShrink: 0, background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {(selectedCandidate.photoVerification.photoUrl || selectedCandidate.photoVerification.photoData) ? (
                        <img
                          src={selectedCandidate.photoVerification.photoUrl || selectedCandidate.photoVerification.photoData}
                          alt="Captured"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            console.error('Image failed to load');
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="2"%3E%3Crect x="2" y="2" width="20" height="20" rx="2"/%3E%3Ccircle cx="12" cy="12" r="4"/%3E%3Cpath d="M22 2L2 22"/%3E%3C/svg%3E';
                            e.target.style.objectFit = 'contain';
                            e.target.style.padding = '20px';
                          }}
                        />
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" width="40" height="40">
                          <rect x="2" y="2" width="20" height="20" rx="2" strokeWidth="2" />
                          <circle cx="12" cy="12" r="4" strokeWidth="2" />
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      {[
                        ['Face Detected', selectedCandidate.photoVerification.faceDetected ? 'Yes ✓' : 'No ✗', selectedCandidate.photoVerification.faceDetected ? '#16A34A' : '#DC2626'],
                        ['Quality', selectedCandidate.photoVerification.quality || 'Unknown', '#111827'],
                        ['Captured At', selectedCandidate.photoVerification.capturedAt ? new Date(selectedCandidate.photoVerification.capturedAt).toLocaleString() : 'Unknown', '#111827'],
                      ].map(([label, val, col]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '5px 0', borderBottom: '1px solid #F3F4F6' }}>
                          <span style={{ color: '#6B7280' }}>{label}</span>
                          <span style={{ color: col, fontWeight: 500 }}>{val}</span>
                        </div>
                      ))}
                      {selectedCandidate.photoVerification.photoUrl && (
                        <div style={{ marginTop: '10px' }}>
                          <a href={selectedCandidate.photoVerification.photoUrl} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '13px', color: '#3B82F6' }}>
                            Open photo in new tab →
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p style={{ color: '#9CA3AF', fontSize: '13px', margin: 0 }}>No photo captured</p>
                )}
              </div>

              {/* Screen Share */}
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.5px', marginBottom: '12px' }}>
                  SCREEN SHARE VERIFICATION
                </div>
                {selectedCandidate.screenShare?.completed ? (
                  <div>
                    {[
                      ['Status', 'Completed', '#16A34A'],
                      ['System Audio Shared', selectedCandidate.screenShare.systemAudioShared ? 'Yes' : 'No', selectedCandidate.screenShare.systemAudioShared ? '#16A34A' : '#9CA3AF'],
                      ['Started At', selectedCandidate.screenShare.startedAt ? new Date(selectedCandidate.screenShare.startedAt).toLocaleString() : 'N/A', '#111827'],
                      ...(selectedCandidate.screenShare.endedAt ? [['Ended At', new Date(selectedCandidate.screenShare.endedAt).toLocaleString(), '#111827']] : []),
                    ].map(([label, val, col]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '5px 0', borderBottom: '1px solid #F3F4F6' }}>
                        <span style={{ color: '#6B7280' }}>{label}</span>
                        <span style={{ color: col, fontWeight: 500 }}>{val}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#9CA3AF', fontSize: '13px', margin: 0 }}>Screen share not completed</p>
                )}
              </div>

              {/* System Check */}
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.5px', marginBottom: '12px' }}>
                  SYSTEM CHECK
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  {[
                    ['Camera', selectedCandidate.systemCheck?.cameraDetected ? '✅ Detected' : '❌ Not Detected'],
                    ['Microphone', selectedCandidate.systemCheck?.microphoneDetected ? '✅ Detected' : '❌ Not Detected'],
                    ['Internet Speed', selectedCandidate.systemCheck?.internetSpeed || 'Unknown'],
                    ['Checked At', selectedCandidate.systemCheck?.checkedAt ? new Date(selectedCandidate.systemCheck.checkedAt).toLocaleString() : 'N/A'],
                  ].map(([label, val], i) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>
                      <span style={{ color: '#6B7280' }}>{label}</span>
                      <span style={{ color: '#111827', fontWeight: 500 }}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Steps Progress */}
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.5px', marginBottom: '12px' }}>
                  PREREQUISITES PROGRESS
                </div>
                {selectedCandidate.steps && selectedCandidate.steps.length > 0 ? (
                  selectedCandidate.steps.map((step, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0',
                      borderBottom: idx < selectedCandidate.steps.length - 1 ? '1px solid #F3F4F6' : 'none'
                    }}>
                      <span style={{
                        width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                        background: step.isCompleted ? '#D1FAE5' : '#F3F4F6',
                        color: step.isCompleted ? '#065F46' : '#6B7280',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: 600
                      }}>
                        {step.stepNumber}
                      </span>
                      <span style={{ flex: 1, color: '#374151', fontSize: '13px' }}>{step.title}</span>
                      <span style={{ fontSize: '13px' }}>
                        {step.isCompleted
                          ? <span style={{ color: '#16A34A' }}>✓ Done</span>
                          : <span style={{ color: '#9CA3AF' }}>○ Pending</span>}
                      </span>
                    </div>
                  ))
                ) : (
                  <p style={{ color: '#9CA3AF', fontSize: '13px', margin: 0 }}>No step data available</p>
                )}
              </div>

              {/* Overall Status */}
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.5px', marginBottom: '12px' }}>
                  OVERALL STATUS
                </div>
                {[
                  ['All Steps Completed', selectedCandidate.allStepsCompleted ? 'Yes ✓' : 'No', selectedCandidate.allStepsCompleted ? '#16A34A' : '#DC2626'],
                  ...(selectedCandidate.completedAt ? [['Completed At', new Date(selectedCandidate.completedAt).toLocaleString(), '#111827']] : []),
                  ...(selectedCandidate.startedAt ? [['Started At', new Date(selectedCandidate.startedAt).toLocaleString(), '#111827']] : []),
                ].map(([label, val, col]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '5px 0', borderBottom: '1px solid #F3F4F6' }}>
                    <span style={{ color: '#6B7280' }}>{label}</span>
                    <span style={{ color: col, fontWeight: 500 }}>{val}</span>
                  </div>
                ))}
              </div>

            </div>

            {/* Modal Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '9px 20px', border: '1px solid #E5E7EB', borderRadius: '8px',
                  background: '#fff', fontSize: '13px', fontWeight: 500,
                  cursor: 'pointer', color: '#374151', fontFamily: 'inherit'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVerificationDashboard;